/**
 * Error screens that replace React Router's built-in "Unexpected Application Error!" page.
 *
 * - <RouteError> is the `errorElement` of the shell routes: inside the shell it renders in <main>
 *   so the navigation stays usable (React Router resets the boundary on the next navigation);
 *   `fullScreen` is used where there is no shell around it (song page, the shell itself).
 * - <AppErrorBoundary> wraps everything App renders, so the screens outside the router (sign-in,
 *   invite link, onboarding) show a reload screen instead of a blank page. Its error screen needs
 *   only <I18nProvider> (useT) — no router, query client, toast or auth context — so main.tsx can
 *   also put it directly inside <I18nProvider>, around the providers and <App/>.
 *
 * A lazy chunk that fails to load (admin chunks are not precached; after a deploy the old hashes
 * are gone) is explained as "not available offline" or "the app needs to reload". Retrying always
 * reloads — React.lazy caches the rejected import — and activates a waiting new version first.
 */
import { House, RotateCw, TriangleAlert, WifiOff, type LucideIcon } from 'lucide-react';
import { Component, useEffect, type ReactNode } from 'react';
import { useNavigate, useRouteError } from 'react-router';
import { isChunkLoadError, reportClientError } from '../errorReporter';
import { useT } from '../i18n';
import { useOnline } from '../lib/useOnline';
import { applyPendingUpdate, checkForUpdate } from '../offline/registerSW';
import { Button, EmptyState } from './ui';

function reloadApp(): void {
  if (!applyPendingUpdate()) window.location.reload();
}

function ErrorView({ error, fullScreen, onHome }: { error: unknown; fullScreen?: boolean; onHome?: () => void }) {
  const t = useT();
  const online = useOnline();
  const chunk = isChunkLoadError(error);

  // A missing chunk while online usually means a deploy: look for the new version right away so
  // "Endurhlaða" can activate it (the update toast appears as well).
  useEffect(() => {
    if (chunk && online) checkForUpdate();
  }, [chunk, online]);

  let icon: LucideIcon = TriangleAlert;
  let title = t('common.error');
  let body = onHome ? t('common.error_body') : t('common.crash_body');
  if (chunk && !online) {
    icon = WifiOff;
    title = t('common.offline_unavailable_title');
    body = t('common.offline_unavailable');
  } else if (chunk) {
    icon = RotateCw;
    title = t('common.chunk_title');
    body = t('common.chunk_body');
  }
  // Offline, reloading cannot bring a missing chunk back; the button returns with the connection.
  const canRetry = !chunk || online;

  const view = (
    <div role="alert" className="w-full">
      <EmptyState
        icon={icon}
        title={title}
        action={
          <div className="mt-2 flex flex-wrap justify-center gap-3">
            {canRetry ? (
              <Button icon={RotateCw} onClick={reloadApp}>
                {chunk ? t('common.reload') : t('common.retry')}
              </Button>
            ) : null}
            {onHome ? (
              <Button variant="secondary" icon={House} onClick={onHome}>
                {t('nav.home')}
              </Button>
            ) : null}
          </div>
        }
      >
        {body}
      </EmptyState>
    </div>
  );

  if (!fullScreen) return <div className="flex min-h-full items-center justify-center">{view}</div>;
  return (
    <div
      className="flex h-dvh w-full items-center justify-center bg-[var(--bg)]"
      style={{
        paddingTop: 'var(--top-inset)',
        paddingBottom: 'var(--safe-bottom)',
        paddingLeft: 'var(--safe-left)',
        paddingRight: 'var(--safe-right)',
      }}
    >
      {view}
    </div>
  );
}

/**
 * `errorElement` for routes. Reporting happens once per error in RouterProvider's onError
 * (reportRouteError), not here: an error element re-renders.
 */
export function RouteError({ fullScreen }: { fullScreen?: boolean }) {
  const error = useRouteError();
  const navigate = useNavigate();
  return <ErrorView error={error} fullScreen={fullScreen} onHome={() => void navigate('/')} />;
}

/** RouterProvider `onError`: called once per caught render error, with the route it happened on. */
export function reportRouteError(error: unknown, info: { location: { pathname: string } }): void {
  reportClientError(error, { route: info.location.pathname, kind: 'render' });
}

/**
 * Boundary for what App renders outside the router's own boundaries (sign-in, invite link,
 * onboarding — React would otherwise unmount the root and leave a white screen). `silent` renders
 * nothing on failure: for background parts (OfflineRuntime) whose crash must not take the UI down.
 */
export class AppErrorBoundary extends Component<{ children: ReactNode; silent?: boolean }, { error: unknown; failed: boolean }> {
  override state = { error: null as unknown, failed: false };

  static getDerivedStateFromError(error: unknown) {
    return { error, failed: true };
  }

  override componentDidCatch(error: unknown) {
    reportClientError(error, { kind: 'render' });
  }

  override render() {
    if (!this.state.failed) return this.props.children;
    return this.props.silent ? null : <ErrorView error={this.state.error} fullScreen />;
  }
}
